import React, { useEffect, useState, useMemo } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  LinearProgress,
} from "@mui/material";
import { X as CloseIcon, Calendar as CalendarIcon, Search as SearchIcon } from "lucide-react";
import { Axios } from "api/axios";
import { API_ENDPOINT } from "../../../helpers/Endpoints/endpoints";
import { MecordColors } from "Constants/Color";
import MasterTableContainer from "Common/Tables/MasterFiltersTable/MasterTableContainer";
import { ColumnFiltersState, SortingState } from "@tanstack/react-table";
import { fetchSalesIndividualInactiveCustomers } from "../services/salesIndividualDashboardApi";
import {
  mergeDashboardFilters,
  applyDashboardClientFilters,
  DashboardColFilter,
  scopedDropdownProps,
} from "./dashboardSidebarFilters";

interface DashboardCustomerSidebarProps {
  open: boolean;
  onClose: () => void;
  type: "product" | "service";
  dateRange: {
    from_date: string;
    to_date: string;
  } | null;
  kpiId?:
    | "new_customer_added"
    | "customer_inactive_over_1_year"
    | "service_customer_inactive_over_1_year";
}

export const DashboardCustomerSidebar: React.FC<DashboardCustomerSidebarProps> = ({
  open,
  onClose,
  type,
  dateRange,
  kpiId = "new_customer_added",
}) => {
  const [data, setData] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [filterValue, setFilterValue] = useState<DashboardColFilter[]>([]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const isInactiveMode =
    kpiId === "customer_inactive_over_1_year" ||
    kpiId === "service_customer_inactive_over_1_year";

  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  const fetchCustomers = async () => {
    if (!dateRange) return;

    setLoading(true);
    try {
      if (isInactiveMode) {
        const response = await fetchSalesIndividualInactiveCustomers(
          {
            period: "custom" as any,
            from_date: dateRange.from_date,
            to_date: dateRange.to_date,
          },
          type,
        );

        let filtered = response.customers || [];
        if (search) {
          const lowerSearch = search.toLowerCase();
          filtered = filtered.filter(
            (c: any) =>
              c.company_name?.toLowerCase().includes(lowerSearch) ||
              c.company_email?.toLowerCase().includes(lowerSearch) ||
              c.phone_number?.toLowerCase().includes(lowerSearch) ||
              c.gstin?.toLowerCase().includes(lowerSearch) ||
              c.branch_name?.toLowerCase().includes(lowerSearch),
          );
        }

        filtered = applyDashboardClientFilters(filtered, filterValue);

        if (sortField) {
          filtered = [...filtered].sort((a, b) => {
            const av = a?.[sortField];
            const bv = b?.[sortField];
            if (av == null && bv == null) return 0;
            if (av == null) return 1;
            if (bv == null) return -1;
            const cmp = String(av).localeCompare(String(bv), undefined, {
              numeric: true,
              sensitivity: "base",
            });
            return sortDirection === "desc" ? -cmp : cmp;
          });
        }

        const start = page * rowsPerPage;
        const end = start + rowsPerPage;
        setData(filtered.slice(start, end));
        setTotalCount(filtered.length);
      } else {
        const url = API_ENDPOINT.MASTERS.CUSTOMER.LIST;
        const baseFilters: any = {
          is_active: true,
          range: {
            created_at: {
              from: dateRange.from_date,
              to: dateRange.to_date,
            },
          },
        };

        const filters = mergeDashboardFilters(baseFilters, filterValue);

        const params = new URLSearchParams();
        params.append("page", String(page + 1));
        params.append("page_size", String(rowsPerPage));
        if (search) params.append("search", search);
        if (sortField) {
          params.append(
            "ordering",
            sortDirection === "desc" ? `-${sortField}` : sortField,
          );
        }

        const response = await Axios.post(`${url}?${params.toString()}`, {
          filters,
        });

        setData(response.data?.data || []);
        setTotalCount(response.data?.totalRecords || response.data?.count || 0);
      }
    } catch (error) {
      console.error("Failed to fetch customers inside dashboard sidebar:", error);
      setData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
      setPage(0);
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  useEffect(() => {
    if (open && dateRange) {
      fetchCustomers();
    }
  }, [
    open,
    type,
    dateRange,
    page,
    rowsPerPage,
    search,
    kpiId,
    filterValue,
    sortField,
    sortDirection,
  ]);

  useEffect(() => {
    if (open) {
      setPage(0);
      setSearchInput("");
      setSearch("");
      setSorting([]);
      setColumnFilters([]);
      setFilterValue([]);
    }
  }, [open, type, dateRange, kpiId]);

  useEffect(() => {
    if (open) setPage(0);
  }, [filterValue, sortField, sortDirection]);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch (e) {
      return dateStr;
    }
  };

  const themeColor = MecordColors?.ThemeBGColor || "#d2363a";
  const headerBgColor = MecordColors?.LightThemeBGColor || "#f8e8e8";

  const kpiBaseFilters = useMemo(() => {
    if (isInactiveMode || !dateRange) return {};
    return {
      is_active: true,
      range: {
        created_at: {
          from: dateRange.from_date,
          to: dateRange.to_date,
        },
      },
    };
  }, [dateRange, isInactiveMode]);

  const columns = useMemo(() => {
    const dropdown = (field: string) =>
      isInactiveMode
        ? { filterType: "text" as const }
        : scopedDropdownProps("customer", field, kpiBaseFilters);

    return [
      {
        header: "Sr. No.",
        accessorKey: "sr_no",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }: { row: any }) =>
          row.original.sr_no || row.index + 1 + page * rowsPerPage,
      },
      {
        header: "Company Name",
        accessorKey: "company_name",
        enableSorting: true,
        ...dropdown("company_name"),
        cell: ({ row }: { row: any }) => (
          <span style={{ fontWeight: 600, color: themeColor }}>
            {row.original.company_name || "-"}
          </span>
        ),
      },
      {
        header: "Branch",
        accessorKey: "branch_name",
        enableSorting: true,
        ...dropdown("branch_name"),
        cell: ({ row }: { row: any }) => row.original.branch_name || "-",
      },
      {
        header: "Email",
        accessorKey: "company_email",
        enableSorting: true,
        ...dropdown("company_email"),
        cell: ({ row }: { row: any }) => row.original.company_email || "-",
      },
      {
        header: "Phone",
        accessorKey: "phone_number",
        enableSorting: true,
        ...dropdown("phone_number"),
        cell: ({ row }: { row: any }) => row.original.phone_number || "-",
      },
      {
        header: "GSTIN",
        accessorKey: "gstin",
        enableSorting: true,
        ...dropdown("gstin"),
        cell: ({ row }: { row: any }) => row.original.gstin || "-",
      },
      {
        header: isInactiveMode ? "Latest Order Date" : "Created Date",
        accessorKey: isInactiveMode ? "latest_order_date" : "created_at",
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) =>
          formatDate(
            isInactiveMode
              ? row.original.latest_order_date
              : row.original.created_at,
          ),
      },
    ];
  }, [themeColor, isInactiveMode, page, rowsPerPage, kpiBaseFilters]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(4px)",
            backgroundColor: "rgba(0, 0, 0, 0.3)",
          },
        },
      }}
      PaperProps={{
        sx: {
          width: "100%",
          "@media (min-width: 768px)": {
            width: "calc(100vw - var(--tb-vertical-menu-width, 250px))",
          },
          p: 3,
          background:
            "linear-gradient(135deg, #ffffff 0%, #fefefe 50%, #f8f9fa 100%)",
          borderTopLeftRadius: "20px",
          borderBottomLeftRadius: "20px",
          borderLeft: "1px solid rgba(0, 0, 0, 0.08)",
          boxShadow:
            "-16px 0 48px rgba(0, 0, 0, 0.16), -4px 0 12px rgba(0, 0, 0, 0.08), inset 1px 0 0 rgba(255, 255, 255, 0.8)",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
          pb: 1.5,
          boxSizing: "border-box",
          fontFamily: "'Poppins', sans-serif",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 2,
            borderBottom: "1px solid #e5e7eb",
            mb: 2.5,
          }}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontFamily: "'Poppins', sans-serif",
                  color: "#111827",
                  fontSize: "18px",
                }}
              >
                {isInactiveMode
                  ? "Inactive Customers Over 1 Year"
                  : "New Customer Added"}
              </Typography>
              <Chip
                label={type === "product" ? "Sales" : "Service"}
                size="small"
                sx={{
                  backgroundColor: headerBgColor,
                  color: themeColor,
                  fontWeight: 600,
                  fontSize: "11px",
                  height: 20,
                  borderRadius: "6px",
                  fontFamily: "'Poppins', sans-serif",
                }}
              />
            </Box>
            {dateRange && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  color: "#6b7280",
                }}
              >
                <CalendarIcon size={12} />
                <Typography
                  sx={{ fontSize: "11px", fontFamily: "'Poppins', sans-serif" }}
                >
                  Period: {formatDate(dateRange.from_date)} to{" "}
                  {formatDate(dateRange.to_date)}
                </Typography>
              </Box>
            )}
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: "#9ca3af" }}>
            <CloseIcon size={20} />
          </IconButton>
        </Box>

        <Box sx={{ mb: 2.5 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by company name, email, phone..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon size={18} style={{ color: "#9ca3af" }} />
                </InputAdornment>
              ),
              endAdornment: searchInput && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchInput("")}
                    edge="end"
                    sx={{ color: "#9ca3af" }}
                  >
                    <CloseIcon size={16} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                backgroundColor: "#f9fafb",
                fontFamily: "'Poppins', sans-serif",
                fontSize: "13px",
                "& fieldset": { borderColor: "#e5e7eb" },
                "&:hover fieldset": { borderColor: "#d1d5db" },
                "&.Mui-focused fieldset": { borderColor: themeColor },
              },
            }}
          />
        </Box>

        <Box
          className="sidebar-table-scroll-container"
          sx={{
            flex: 1,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            border: "1px solid #e5e7eb",
            borderRadius: "12px",
            backgroundColor: "#ffffff",
            boxShadow: "inset 0 1px 2px rgba(0, 0, 0, 0.02)",
            position: "relative",
          }}
        >
          {loading && (
            <LinearProgress
              sx={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 10 }}
            />
          )}
          <MasterTableContainer
            columns={columns}
            data={data}
            totalRecords={totalCount}
            pageIndex={page}
            pageSize={rowsPerPage}
            onPageChange={setPage}
            onPageSizeChange={setRowsPerPage}
            sorting={sorting}
            onSortingChange={setSorting}
            manualSorting={true}
            columnFilters={columnFilters}
            setColumnFilters={setColumnFilters}
            colValue={setFilterValue}
          />
        </Box>
      </Box>
    </Drawer>
  );
};
